import type { MapMouseEvent } from 'mapbox-gl';
import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import useMap from '../../../hooks/useMap';
import { useAppDispatch, useAppSelector } from '../../../redux';
import { addActiveSegmentPath, selectCustomRouteActiveSegment, setToastMessage } from '../../../redux/customRoute';
import { updateCursor } from '../../../redux/reducer';
import { LatLng } from '../../../types/Geo';
import { isSinglePointSegment } from '../../../utils/customRoutes/validation';
import { hasMovedMinDistance, smoothFreeDrawPath } from './freeDrawSmoothing';
import { validatePathLength, validateProximityToPreviousPoint } from './validationUtils';

const CURSOR_FREE_DRAW = 'crosshair';

const useFreeDraw = (isSmartRoutingRef: MutableRefObject<boolean>) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const map = useMap();
  const activeSegment = useAppSelector(selectCustomRouteActiveSegment);
  const selectedHandleIndex = useAppSelector(state => state.customRoute.selectedHandleIndex);

  // State that should not trigger a re-render
  const isStartingRef = useRef(false);
  const isPendingRef = useRef(false);
  const lastDrawnPointRef = useRef<LatLng | undefined>(undefined);
  // The latest cursor position that was too close to the last point to add, so the path still ends at the cursor on release
  const trailingPointRef = useRef<LatLng | undefined>(undefined);

  // State that should trigger a re-render
  const [isSaving, setIsSaving] = useState(false);
  const [points, setPoints] = useState<LatLng[] | undefined>(undefined);

  // Add the next point to the free draw path
  const addPoint = useCallback(
    (latitude: number, longitude: number) => {
      setPoints(prev => {
        const newPoints = prev ? [...prev] : [];

        // If this is the first point in the free draw path and it doesn't match the last point in the active segment, insert the last point of the active segment
        // to prevent a gap between the last point of the active segment and the first point of the free draw path
        if (!prev && activeSegment && selectedHandleIndex !== undefined) {
          const selectedPoint =
            selectedHandleIndex === activeSegment.paths.length
              ? activeSegment.paths[activeSegment.paths.length - 1].points[activeSegment.paths[activeSegment.paths.length - 1].points.length - 1]
              : activeSegment.paths[selectedHandleIndex].points[0];

          if (selectedPoint && (latitude !== selectedPoint[0] || longitude !== selectedPoint[1])) {
            const newPoint: [number, number] = [latitude, longitude];
            if (!validateProximityToPreviousPoint(newPoint, selectedPoint)) {
              dispatch(setToastMessage({ iconCode: 'error', message: intl.formatMessage({ defaultMessage: 'Tap or draw closer to your route' }) }));
              return prev;
            }

            if (!(selectedHandleIndex === 0 && isSinglePointSegment(activeSegment))) {
              newPoints.unshift(selectedPoint);
            }
          }
        }

        // Validate proximity to the previous point if there is one
        if (newPoints.length > 0) {
          const lastPoint = newPoints[newPoints.length - 1];
          const newPoint: [number, number] = [latitude, longitude];

          if (!validateProximityToPreviousPoint(newPoint, lastPoint)) {
            // Don't add the point if it's too far from the previous one
            dispatch(setToastMessage({ iconCode: 'error', message: intl.formatMessage({ defaultMessage: 'Tap or draw closer to your route' }) }));
            return prev;
          }
        }

        return [...newPoints, [latitude, longitude]];
      });
    },
    // Changes to activeSegment will cause re-registration of event handlers but this should be rare since drawing changes don't update the active segment until drawing is complete
    [activeSegment, selectedHandleIndex, dispatch, intl]
  );

  const start = useCallback(
    (event: MapMouseEvent) => {
      isStartingRef.current = true;
      lastDrawnPointRef.current = undefined;
      trailingPointRef.current = undefined;
      dispatch(updateCursor(CURSOR_FREE_DRAW));

      // Prevent default behavior to avoid map panning
      event.preventDefault();
    },
    [dispatch]
  );

  // Update free draw state and add a point if free draw conditions are met
  const update = useCallback(
    (event: MapMouseEvent) => {
      const {
        lngLat: { lng, lat }
      } = event;

      if (isStartingRef.current) {
        isPendingRef.current = true;
        isStartingRef.current = false;
      }

      if (isPendingRef.current) {
        if (isSmartRoutingRef.current) {
          // Deferred to BYOT Phase 3
        } else {
          const point: LatLng = [lat, lng];
          const zoom = map?.getZoom();

          if (lastDrawnPointRef.current && zoom !== undefined && !hasMovedMinDistance(lastDrawnPointRef.current, point, zoom)) {
            trailingPointRef.current = point;
            return;
          }

          lastDrawnPointRef.current = point;
          trailingPointRef.current = undefined;
          addPoint(lat, lng);
        }
      }
    },
    [isSmartRoutingRef, addPoint, map]
  );

  // Save the free draw path to the redux store when the user is done drawing
  // We use a flag to trigger saving instead of doing it in the callback to prevent re-registering event handlers
  // Keep the dependency array empty to prevent re-registering event handlers
  const save = useCallback(() => {
    isStartingRef.current = false;
    isPendingRef.current = false;
    setIsSaving(true);
  }, []);

  // Save changes when free drawing has ended
  useEffect(() => {
    if (!isSaving) {
      return;
    }

    const trailingPoint = trailingPointRef.current;
    lastDrawnPointRef.current = undefined;
    trailingPointRef.current = undefined;

    if (points && points.length > 0) {
      const drawnPoints = trailingPoint ? [...points, trailingPoint] : points;
      const zoom = map?.getZoom();
      const smoothedPoints = zoom === undefined ? drawnPoints : smoothFreeDrawPath(drawnPoints, zoom);

      // Validate path length
      if (!validatePathLength(smoothedPoints)) {
        dispatch(setToastMessage({ iconCode: 'error', message: intl.formatMessage({ defaultMessage: 'Draw a shorter segment' }) }));
        setIsSaving(false);
        setPoints(undefined);
        return;
      }
      // If drawing from the start point the new path's direction will be opposite that of the draw direction
      dispatch(
        addActiveSegmentPath({
          points: selectedHandleIndex === 0 && !isSinglePointSegment(activeSegment!) ? [...smoothedPoints].reverse() : smoothedPoints,
          freeDraw: true
        })
      );
    }

    // Clear the save flag and the drawn path once it is saved
    setIsSaving(false);
    setPoints(undefined);
  }, [dispatch, points, isSaving, selectedHandleIndex, activeSegment, intl, map]);

  return {
    isStartingRef,
    isPendingRef,
    points,
    start,
    update,
    save
  };
};

export default useFreeDraw;
